import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { Task } from '../types'
import { getTasks, updateTask } from '../services/tasks.service'
import { supabase } from '../lib/supabase'

interface RealtimeContextValue {
  tasks: Task[]
  addTask: (task: Task) => void
  updateTaskStatus: (id: string, status: Task['status']) => void
  replaceTasks: (tasks: Task[]) => void
  tasksFor: (name: string) => Task[]
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null)

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([])

  // Initial load from Supabase
  useEffect(() => {
    getTasks().then(setTasks).catch(console.error)
  }, [])

  // Supabase realtime subscription on tasks table
  useEffect(() => {
    const channel = supabase
      .channel('tasks-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
        // Reload all tasks on any change
        getTasks().then(setTasks).catch(console.error)
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [])

  const addTask = useCallback((task: Task) => {
    // Optimistic update — actual persistence is handled by the caller (createTask)
    setTasks(prev => {
      if (prev.some(t => t.id === task.id)) return prev // avoid duplicate from realtime event
      return [task, ...prev]
    })
  }, [])

  const updateTaskStatus = useCallback((id: string, status: Task['status']) => {
    // Optimistic update
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t))
    // Persist to Supabase
    updateTask(id, { status }).catch(console.error)
  }, [])

  const replaceTasks = useCallback((newTasks: Task[]) => {
    setTasks(newTasks)
  }, [])

  const tasksFor = useCallback((name: string) => {
    return tasks.filter(t => t.assignee === name)
  }, [tasks])

  return (
    <RealtimeContext.Provider value={{ tasks, addTask, updateTaskStatus, replaceTasks, tasksFor }}>
      {children}
    </RealtimeContext.Provider>
  )
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext)
  if (!ctx) throw new Error('useRealtime must be used inside <RealtimeProvider>')
  return ctx
}
